import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Tooltip, Space, Dropdown } from "antd";
import { checkParentElementHasClassName, getAIServiceBaseURL, getFromLS, updateConfigHeaderForDemoUseStagingAI } from "lib/utils/helperMethods";
import { useTranslate } from "lib/MrTranslate/MrTranslate";
import { SoundOutlined, PauseOutlined, DownOutlined, CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import "./TextToSpeech.scss";

const TextToSpeech = () => {
  const [audioBlob, setAudioBlob] = useState(null);
  const [audio, setAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [selectedTTSGenderVoice, setSelectedTTSGenderVoice] = useState({
    voice: "female",
    name: "en-US-AvaMultilingualNeural"
  })
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  let previousSelectedText = ""; // track the last selection

  const textToSpeechAPIEndpoint = `${getAIServiceBaseURL()}/tts/cognitiveservices/v1`;
  const token = getFromLS("token");
  const mrIntl = useTranslate();
  const items = [
    {
      key: 'gender-selector',
      type: 'group',
      label: mrIntl("TextToSpeech.select_voice"),
      children: [
        {
          key: 'gender-male',
          label: mrIntl("TextToSpeech.male"),
          icon: selectedTTSGenderVoice.voice === 'male' ? <CheckOutlined /> : <span></span>,
          onClick: () => handleVoiceChange({ key: 'gender-male' }, false)
        },
        {
          key: 'gender-female',
          label: mrIntl("TextToSpeech.female"),
          icon: selectedTTSGenderVoice.voice === 'female' ? <CheckOutlined /> : <span></span>,
          onClick: () => handleVoiceChange({ key: 'gender-female' }, false)
        }
      ]
    },
    
  ];

  useEffect(() => {
    document.addEventListener("selectionchange", handleSelectionChange);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isLoading]);

  useEffect(() => {
    if (audioBlob && isPlaying) {
      playAudio();
    } else {
      pauseAudio();
    }
  }, [audioBlob, isPlaying]);

  const handleSelectionChange = () => {
    try {
      const selection = window.getSelection() || "";
      const selectedTextToSpeak = selection.toString().trim();

      // Check if the new selection is different from the previous selection
      if (selectedTextToSpeak && selectedTextToSpeak !== previousSelectedText) {
        // Clear the previous audio only if new text is selected
        setAudioBlob(null);
        setAudio(null);
      }

      let finalTextToSpeak = "";

      // Iterate over each range within the selection
      for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        // Concatenate the text from the range and any alt text from images
        finalTextToSpeak += getTextAndAltTextFromRange(range) + " ";
      }

      // Trim the final string of text
      finalTextToSpeak = finalTextToSpeak.trim();

      // Update the selectedText state with the combined text
      if (finalTextToSpeak) {
        setSelectedText(finalTextToSpeak);
        // Remember the new selection
        previousSelectedText = finalTextToSpeak;
      }

    } catch (error) {
      console.error('Error occurred in handleSelectionChange:', error);
    }
  };
  
  const getTextAndAltTextFromRange = (range) => {
    // Initialize a variable to hold the combined text
    let text = '';
    // Clone the contents of the range into a document fragment for processing
    const contents = range.cloneContents();
    // Create a TreeWalker to traverse the text and element nodes within the document fragment
    const walker = document.createTreeWalker(
      contents,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, // Specify node types to be returned by the TreeWalker
      null, // Use no filter (a filter function could be used to further refine the nodes returned)
      false // The fourth argument is obsolete and should be false for backward compatibility
    );

    // Start with the first node in the traversal
    let currentNode = walker.currentNode;
    // Loop through the nodes as long as there are nodes to process
    while (currentNode) {
      // If the current node is a text node, concatenate its trimmed text content
      if (currentNode.nodeType === Node.TEXT_NODE) {
        text += currentNode.textContent.trim() + " ";
      // If it's an element node with an 'alt' attribute, concatenate its trimmed 'alt' text
      } else if (currentNode.nodeType === Node.ELEMENT_NODE && currentNode.hasAttribute('alt')) {
        text += currentNode.getAttribute('alt').trim() + " ";
      }
      // Move to the next node in the document fragment
      currentNode = walker.nextNode();
    }
    text = text.replace(/space/g, "");
    // Return the concatenated text with leading and trailing whitespace removed
    return text.trim();
  }

  const handleMouseDown = (e) => {
    // We want to avoid executing the code below when clicking on the TTS Button. 
    const isClassExistOfWrapper = checkParentElementHasClassName(e.target, "text-to-speech-wrapper");
    if (isClassExistOfWrapper) {
      e.preventDefault();
    }
    // Hide the dropdown and clear the selected text if clicking outside the TTS menu
    const isClassExistOfMenu = checkParentElementHasClassName(e.target, "text-to-speech-gender-selector");
    if (!isClassExistOfMenu && !isClassExistOfWrapper) {
      setDropdownVisible(false);
      if (!isPlaying && !isLoading) {
        setSelectedText(null);
      }
    }
    // Check for clicks on back or end buttons and clear states
    const classList = e.target.classList;
    const isClassExitOfBackButton = checkParentElementHasClassName(e.target, "page-header-back-button");
    if (isClassExitOfBackButton || classList.contains("page-header-back-button") || classList.contains("end-test-button")) {
      setAudioBlob(null);
      setAudio(null);
      setSelectedText(null);
    }
  };

  const manageTextToSpeechPlayback = async () => {
    if (!audioBlob) {
      // Make API request if audioBlob is not available
      try {
        setIsLoading(true);
        const text = selectedText.replace(/&nbsp;|<br>/g, ' ');
        const speechSynthesisMarkupLanguage = `<speak version='1.0' xml:lang='en-us'>
                      <voice xml:lang='en-us' xml:gender='${selectedTTSGenderVoice.voice}' name='${selectedTTSGenderVoice.name}'>
                        ${text}
                      </voice>
                    </speak>`;
        let config = {
          method: "POST",
          url: textToSpeechAPIEndpoint,
          headers: {
            "Content-Type": "application/ssml+xml",
            "X-Microsoft-OutputFormat": "audio-16khz-32kbitrate-mono-mp3",
            Authorization: token,
          },
          data: speechSynthesisMarkupLanguage,
          responseType: "arraybuffer",
        };
        config = updateConfigHeaderForDemoUseStagingAI(config)
        const response = await axios.request(config);
        // Convert array buffer to stream and create a ReadableStream from the response data received from the Text-to-Speech API.
        // This stream allows for the efficient reading of the audio data in chunks.
        const arrayBufferStream = new ReadableStream({
          start(controller) {
            // Enqueue the response data (converted to Uint8Array) into the stream controller.
            controller.enqueue(new Uint8Array(response.data));
            controller.close();
          },
        });
        // Convert the ReadableStream containing audio data into a File.
        // Creating a File object named 'audio.mp3' from the binary data received from the Response object
        // The binary data is obtained by converting the ReadableStream 'arrayBufferStream' to an ArrayBuffer
        // The File object represents an audio file with MIME type 'audio/mpeg'
        const audioData = new File([await new Response(arrayBufferStream).arrayBuffer()], 'audio.mp3', { type: 'audio/mpeg' });

        setIsPlaying(true)
        setAudioBlob(audioData);
        setIsLoading(false);
      } catch (error) {
        console.log(error);
      }
    } else {
      setIsPlaying((prevIsPlaying) => !prevIsPlaying);
    }
  };

  const playAudio = () => {
    if (!audioBlob) {
      return;
    }
    if (!audio) {
      const newAudio = new Audio(URL.createObjectURL(audioBlob));
      newAudio.onended = () => {
        setIsPlaying(false);
        setSelectedText(null)
      };
      newAudio.play();
      setAudio(newAudio);
    } else {
      audio.play();
    }
  };

  const pauseAudio = () => {
    if (audio) {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleVoiceChange = ({key}) => {
    let voice;
    let name;
    switch (key) {
      case 'gender-male':
        voice = "male";
        name = 'en-US-RyanMultilingualNeural';
        break;
      case 'gender-female':
        voice = "female";
        name = 'en-US-AvaMultilingualNeural';
        break;
      default:
        voice = 'female';
        name = 'en-US-AvaMultilingualNeural';
    }
    setSelectedTTSGenderVoice({
      voice: voice,
      name: name
    });
    setDropdownVisible(true);
  };

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      setDropdownVisible(nextOpen);
    }
  };

  return <>
    <Space className="text-to-speech-wrapper">
      <div ref={dropdownRef}>
        <Dropdown.Button
          overlayClassName="text-to-speech-gender-selector"
          menu={{
            items,
            onClick: (value) => handleVoiceChange(value, false),
            defaultChecked: selectedTTSGenderVoice.voice,
          }}
          onOpenChange={handleOpenChange}
          open={dropdownVisible}
          buttonsRender={([leftButton, rightButton]) => [
            <Tooltip title={!selectedText ? mrIntl("TextToSpeech.select_text_then_click_to_read_aloud") : isPlaying ? mrIntl("CommonText.pause") : mrIntl("TextToSpeech.read_aloud")} key="left-button">
              {leftButton}
            </Tooltip>,
            rightButton
          ]}
          disabled={!selectedText || isLoading}
          onClick={() => manageTextToSpeechPlayback()}
          icon={<DownOutlined />}
          placement="bottomRight"
        > {isLoading ? <LoadingOutlined /> : isPlaying ? <PauseOutlined /> : <SoundOutlined />}
          {isPlaying ? mrIntl("CommonText.pause") : mrIntl("TextToSpeech.speak")}
        </Dropdown.Button>
      </div>
    </Space>
  </>;
};
export default TextToSpeech;